export const BASE_LINE_LABEL = '基準 (Base)';
export const COMPARISON_LINE_LABEL = '比較 (Comparison)';
export const PEAK_MATCH_TOLERANCE_HZ = 5;
export const FREQUENCY_DIFF_NORMAL_LIMIT_PERCENT = 10;
export const AMPLITUDE_DIFF_NORMAL_LIMIT_PERCENT = 15;
export const MACHINE_DIAGNOSIS_AXES = ['X', 'Y', 'Z'];

const exceedsPercentageLimit = (value, limit) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > limit;
};

export const isFrequencyDifferenceAbnormal = (value) =>
  exceedsPercentageLimit(value, FREQUENCY_DIFF_NORMAL_LIMIT_PERCENT);

export const isAmplitudeDifferenceAbnormal = (value) =>
  exceedsPercentageLimit(value, AMPLITUDE_DIFF_NORMAL_LIMIT_PERCENT);

export const hasComparableDifference = (value) =>
  value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value));

export const getComparisonStatus = (frequencyDifferences, amplitudeDifferences) => {
  if (!Array.isArray(frequencyDifferences) || !Array.isArray(amplitudeDifferences)) {
    return null;
  }

  let hasComparableRow = false;
  const rowCount = Math.max(frequencyDifferences.length, amplitudeDifferences.length);

  for (let index = 0; index < rowCount; index += 1) {
    const frequencyDifference = frequencyDifferences[index];
    const amplitudeDifference = amplitudeDifferences[index];

    if (!hasComparableDifference(frequencyDifference) || !hasComparableDifference(amplitudeDifference)) {
      continue;
    }

    hasComparableRow = true;
    if (isFrequencyDifferenceAbnormal(frequencyDifference) ||
        isAmplitudeDifferenceAbnormal(amplitudeDifference)) {
      return 'NG';
    }
  }

  return hasComparableRow ? 'GO' : null;
};

const getAxisData = (machine, axis) => machine?.[`updata${axis}`] || null;

const getValidPeakRows = (axisData) => {
  if (!axisData ||
      !Array.isArray(axisData.Amp_peak_freq_data) ||
      !Array.isArray(axisData.Amp_peak_data)) {
    return [];
  }

  return axisData.Amp_peak_freq_data
    .map((frequency, index) => ({
      frequency: Number(frequency),
      amplitude: Number(axisData.Amp_peak_data[index])
    }))
    .filter(({ frequency, amplitude }) =>
      Number.isFinite(frequency) && Number.isFinite(amplitude)
    );
};

export const getAxisThresholdComparison = (machine, baselineMachine, axis) => {
  const axisName = String(axis || '').toUpperCase();
  const currentRows = getValidPeakRows(getAxisData(machine, axisName));
  const baselineRows = getValidPeakRows(getAxisData(baselineMachine, axisName));
  const baselineFrequencies = baselineRows.map(({ frequency }) => frequency);

  if (currentRows.length === 0 || baselineRows.length === 0) {
    return {
      status: null,
      frequencyDifferences: [],
      amplitudeDifferences: []
    };
  }

  const frequencyDifferences = [];
  const amplitudeDifferences = [];

  currentRows.forEach(({ frequency, amplitude }) => {
    const matchIndex = findNearestPeakIndex(
      frequency,
      baselineFrequencies,
      PEAK_MATCH_TOLERANCE_HZ
    );

    if (matchIndex === -1) {
      frequencyDifferences.push(null);
      amplitudeDifferences.push(null);
      return;
    }

    const baselineFrequency = baselineRows[matchIndex].frequency;
    const baselineAmplitude = baselineRows[matchIndex].amplitude;

    if (baselineFrequency === 0 || baselineAmplitude === 0) {
      frequencyDifferences.push(null);
      amplitudeDifferences.push(null);
      return;
    }

    frequencyDifferences.push(
      Math.abs((frequency - baselineFrequency) / baselineFrequency) * 100
    );
    amplitudeDifferences.push(
      Math.abs((amplitude - baselineAmplitude) / baselineAmplitude) * 100
    );
  });

  return {
    status: getComparisonStatus(frequencyDifferences, amplitudeDifferences),
    frequencyDifferences,
    amplitudeDifferences
  };
};

export const getAxisThresholdStatus = (machine, baselineMachine, axis) =>
  getAxisThresholdComparison(machine, baselineMachine, axis).status;

export const getMachineThresholdDiagnosis = (entries, machine) => {
  const baselineMachine = findBaselineForMachineModel(entries, machine?.machineModel);
  const axesDetails = {};

  MACHINE_DIAGNOSIS_AXES.forEach((axis) => {
    axesDetails[axis] = getAxisThresholdComparison(machine, baselineMachine, axis);
  });

  const axisStatuses = MACHINE_DIAGNOSIS_AXES.map((axis) => axesDetails[axis].status);
  const hasNgAxis = axisStatuses.includes('NG');
  const allAxesGo = axisStatuses.every((status) => status === 'GO');

  return {
    method: 'threshold',
    baselineMachineId: baselineMachine?._id || null,
    overallResult: hasNgAxis ? 'NG' : (allAxesGo ? 'GO' : null),
    axesDetails
  };
};

export const getMachineModelFamily = (machineModel) => {
  const normalizedModel = String(machineModel || '').trim().toLowerCase();
  return normalizedModel.split('-')[0].trim();
};

export const findBaselineForMachineModel = (entries, machineModel) => {
  const modelFamily = getMachineModelFamily(machineModel);
  if (!modelFamily || !Array.isArray(entries)) {
    return null;
  }

  return entries
    .filter((entry) => getMachineModelFamily(entry?.machineModel) === modelFamily)
    .sort((left, right) => String(left?._id || '').localeCompare(String(right?._id || '')))[0] || null;
};

export const findNearestPeakIndex = (
  frequency,
  baselineFrequencies,
  toleranceHz = PEAK_MATCH_TOLERANCE_HZ
) => {
  const targetFrequency = Number(frequency);
  const tolerance = Number(toleranceHz);

  if (!Number.isFinite(targetFrequency) || !Array.isArray(baselineFrequencies) ||
      !Number.isFinite(tolerance) || tolerance < 0) {
    return -1;
  }

  let nearestIndex = -1;
  let nearestDifference = Infinity;

  baselineFrequencies.forEach((baselineFrequency, index) => {
    const numericBaseline = Number(baselineFrequency);
    if (!Number.isFinite(numericBaseline)) return;

    const difference = Math.abs(targetFrequency - numericBaseline);
    if (difference <= tolerance && difference < nearestDifference) {
      nearestIndex = index;
      nearestDifference = difference;
    }
  });

  return nearestIndex;
};
